import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  BarChart3, TrendingUp, TrendingDown, DollarSign, ShoppingCart,
  Clock, RefreshCw, Loader2, Flame, ArrowUpRight, ArrowDownRight,
  Utensils, Truck, Store, Package, Download
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, CartesianGrid, Legend,
  AreaChart, Area
} from 'recharts';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = ['#D97706', '#2563EB', '#10B981', '#EF4444', '#7C3AED', '#EC4899', '#0891B2', '#F97316'];
const HEATMAP_COLORS = ['#1e293b', '#1e3a5f', '#1d4ed8', '#3b82f6', '#60a5fa', '#93c5fd', '#f59e0b', '#f97316', '#ef4444'];

function fmt(v) {
  return Number(v || 0).toLocaleString('ar-SA', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
}

function KPICard({ icon: Icon, label, value, suffix, growth, color, testId }) {
  const isPositive = growth > 0;
  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-all" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-2.5 rounded-xl ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          {growth !== undefined && growth !== null && (
            <Badge variant="outline" className={`text-xs font-medium ${isPositive ? 'text-emerald-600 border-emerald-300 bg-emerald-50' : 'text-red-600 border-red-300 bg-red-50'}`}>
              {isPositive ? <ArrowUpRight className="h-3 w-3 me-0.5" /> : <ArrowDownRight className="h-3 w-3 me-0.5" />}
              {Math.abs(growth)}%
            </Badge>
          )}
        </div>
        <div className="text-2xl font-bold tracking-tight">{value} <span className="text-sm font-normal text-muted-foreground">{suffix}</span></div>
        <div className="text-xs text-muted-foreground mt-1">{label}</div>
      </CardContent>
    </Card>
  );
}

function HeatmapChart({ data, t }) {
  if (!data || data.length === 0) return <EmptyState text={t('orderAnalytics.noData')} />;

  const dayNames = t('orderAnalytics.days', { returnObjects: true }) || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const maxCount = Math.max(...data.map(d => d.count), 1);

  // Build grid: 7 days × 24 hours
  const grid = {};
  data.forEach(d => { grid[`${d.day}-${d.hour}`] = d.count; });

  const getColor = (count) => {
    if (count === 0) return 'bg-muted/30';
    const ratio = count / maxCount;
    if (ratio < 0.2) return 'bg-blue-900/40';
    if (ratio < 0.4) return 'bg-blue-700/50';
    if (ratio < 0.6) return 'bg-amber-600/50';
    if (ratio < 0.8) return 'bg-orange-500/60';
    return 'bg-red-500/70';
  };

  const hours = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

  return (
    <div className="overflow-x-auto" data-testid="heatmap-chart">
      <div className="min-w-[600px]">
        {/* Header hours */}
        <div className="flex gap-1 mb-1 ps-20">
          {hours.map(h => (
            <div key={h} className="flex-1 text-center text-[10px] text-muted-foreground">{h}</div>
          ))}
        </div>
        {/* Grid rows */}
        {dayNames.map((day, dayIdx) => (
          <div key={dayIdx} className="flex gap-1 mb-1 items-center">
            <div className="w-20 text-xs text-muted-foreground text-end pe-2 shrink-0">{day}</div>
            {hours.map(h => {
              const count = grid[`${dayIdx}-${h}`] || 0;
              return (
                <div
                  key={h}
                  className={`flex-1 h-7 rounded-sm ${getColor(count)} transition-all hover:ring-1 hover:ring-primary cursor-default`}
                  title={`${day} ${h}:00 - ${count} ${t('orderAnalytics.orders')}`}
                />
              );
            })}
          </div>
        ))}
        {/* Legend */}
        <div className="flex items-center gap-2 mt-3 ps-20">
          <span className="text-[10px] text-muted-foreground">{t('orderAnalytics.less')}</span>
          {['bg-muted/30', 'bg-blue-900/40', 'bg-blue-700/50', 'bg-amber-600/50', 'bg-orange-500/60', 'bg-red-500/70'].map((c, i) => (
            <div key={i} className={`w-5 h-3 rounded-sm ${c}`} />
          ))}
          <span className="text-[10px] text-muted-foreground">{t('orderAnalytics.more')}</span>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
      <Package className="h-5 w-5 me-2 opacity-50" />
      {text}
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg p-3 shadow-lg text-sm">
      <div className="font-medium mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-medium">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function OrderAnalyticsPage() {
  const { authHeaders, activeBranch } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [downloading, setDownloading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!activeBranch) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/analytics/orders-deep?branch_id=${activeBranch.id}&period=${period}`, authHeaders());
      setData(res.data);
    } catch (e) {
      toast.error(t('common.failed'));
    } finally {
      setLoading(false);
    }
  }, [activeBranch, period, authHeaders, t]);

  const downloadPdf = async () => {
    if (!activeBranch) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/api/analytics/orders-deep/export-pdf?branch_id=${activeBranch.id}&period=${period}`, {
        ...authHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `zouqai_orders_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('orderAnalytics.pdfDownloaded'));
    } catch {
      toast.error(t('common.failed'));
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return (
      <ModuleLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ModuleLayout>
    );
  }

  const kpis = data?.kpis || {};

  return (
    <ModuleLayout>
      <div className="space-y-6 p-4 sm:p-6" data-testid="order-analytics-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{t('orderAnalytics.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('orderAnalytics.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            {['7', '14', '30'].map(p => (
              <Button
                key={p}
                variant={period === p ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriod(p)}
                data-testid={`period-btn-${p}`}
              >
                {p} {t('orderAnalytics.daysLabel')}
              </Button>
            ))}
            <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloading} data-testid="download-pdf-btn">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="kpi-cards">
          <KPICard icon={DollarSign} label={t('orderAnalytics.totalRevenue')} value={fmt(kpis.total_revenue)} suffix={t('common.sar')} growth={kpis.revenue_growth} color="bg-emerald-600" testId="kpi-revenue" />
          <KPICard icon={ShoppingCart} label={t('orderAnalytics.totalOrders')} value={fmt(kpis.total_orders)} growth={kpis.orders_growth} color="bg-blue-600" testId="kpi-orders" />
          <KPICard icon={BarChart3} label={t('orderAnalytics.avgOrderValue')} value={fmt(kpis.avg_order_value)} suffix={t('common.sar')} color="bg-purple-600" testId="kpi-avg" />
          <KPICard icon={Clock} label={t('orderAnalytics.peakHour')} value={`${kpis.peak_hour}:00`} color="bg-orange-600" testId="kpi-peak" />
          <KPICard icon={Flame} label={t('orderAnalytics.todayRevenue')} value={fmt(kpis.today_revenue)} suffix={t('common.sar')} color="bg-red-600" testId="kpi-today" />
        </div>

        {/* Revenue Trend Chart */}
        <Card className="border-0 shadow-sm" data-testid="revenue-trend-card">
          <CardContent className="p-4 sm:p-6">
            <h3 className="font-semibold mb-4">{t('orderAnalytics.revenueTrend')}</h3>
            {data?.daily_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.daily_trend}>
                  <defs>
                    <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#D97706" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#D97706" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="revenue" name={t('orderAnalytics.revenue')} stroke="#D97706" fill="url(#revenueGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <EmptyState text={t('orderAnalytics.noData')} />}
          </CardContent>
        </Card>

        {/* Row: Heatmap + Week Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Heatmap */}
          <Card className="border-0 shadow-sm" data-testid="heatmap-card">
            <CardContent className="p-4 sm:p-6">
              <h3 className="font-semibold mb-4">{t('orderAnalytics.peakHours')}</h3>
              <HeatmapChart data={data?.heatmap || []} t={t} />
            </CardContent>
          </Card>

          {/* Week Comparison */}
          <Card className="border-0 shadow-sm" data-testid="week-comparison-card">
            <CardContent className="p-4 sm:p-6">
              <h3 className="font-semibold mb-4">{t('orderAnalytics.weekComparison')}</h3>
              {data?.week_comparison?.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.week_comparison}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="this_week" name={t('orderAnalytics.thisWeek')} fill="#D97706" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="last_week" name={t('orderAnalytics.lastWeek')} fill="#94A3B8" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyState text={t('orderAnalytics.noData')} />}
            </CardContent>
          </Card>
        </div>

        {/* Row: Combos + Channel Breakdown + Size Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Top Combos */}
          <Card className="border-0 shadow-sm" data-testid="combos-card">
            <CardContent className="p-4 sm:p-6">
              <h3 className="font-semibold mb-4">{t('orderAnalytics.topCombos')}</h3>
              {data?.top_combos?.length > 0 ? (
                <div className="space-y-3">
                  {data.top_combos.map((combo, i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`combo-item-${i}`}>
                      <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{combo.item_a}</div>
                        <div className="text-xs text-muted-foreground">+ {combo.item_b}</div>
                      </div>
                      <Badge variant="secondary" className="shrink-0">{combo.count}x</Badge>
                    </div>
                  ))}
                </div>
              ) : <EmptyState text={t('orderAnalytics.noCombos')} />}
            </CardContent>
          </Card>

          {/* Channel Breakdown */}
          <Card className="border-0 shadow-sm" data-testid="channel-card">
            <CardContent className="p-4 sm:p-6">
              <h3 className="font-semibold mb-4">{t('orderAnalytics.channelBreakdown')}</h3>
              {data?.channel_breakdown?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={data.channel_breakdown.filter(c => c.orders > 0)}
                        cx="50%" cy="50%"
                        innerRadius={50} outerRadius={75}
                        dataKey="orders"
                        nameKey="name"
                        paddingAngle={3}
                      >
                        {data.channel_breakdown.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2 mt-2">
                    {data.channel_breakdown.map((ch, i) => {
                      const icons = [Utensils, Truck, Store];
                      const ChIcon = icons[i] || Store;
                      return (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[i] }} />
                            <ChIcon className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{ch.name}</span>
                          </div>
                          <div className="text-muted-foreground">{ch.orders} | {fmt(ch.revenue)} {t('common.sar')}</div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : <EmptyState text={t('orderAnalytics.noData')} />}
            </CardContent>
          </Card>

          {/* Order Size Distribution */}
          <Card className="border-0 shadow-sm" data-testid="size-dist-card">
            <CardContent className="p-4 sm:p-6">
              <h3 className="font-semibold mb-4">{t('orderAnalytics.orderSize')}</h3>
              {data?.size_distribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.size_distribution} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="size" type="category" tick={{ fontSize: 11 }} width={70} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name={t('orderAnalytics.orders')} fill="#6366F1" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <EmptyState text={t('orderAnalytics.noData')} />}
            </CardContent>
          </Card>
        </div>

        {/* Top Items Table */}
        <Card className="border-0 shadow-sm" data-testid="top-items-card">
          <CardContent className="p-4 sm:p-6">
            <h3 className="font-semibold mb-4">{t('orderAnalytics.topItems')}</h3>
            {data?.top_items?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-start py-2 text-muted-foreground font-medium">#</th>
                      <th className="text-start py-2 text-muted-foreground font-medium">{t('orderAnalytics.itemName')}</th>
                      <th className="text-start py-2 text-muted-foreground font-medium">{t('orderAnalytics.quantity')}</th>
                      <th className="text-start py-2 text-muted-foreground font-medium">{t('orderAnalytics.revenue')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_items.map((item, i) => (
                      <tr key={i} className="border-b border-border/50 hover:bg-muted/30 transition-colors" data-testid={`top-item-${i}`}>
                        <td className="py-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>{i + 1}</div>
                        </td>
                        <td className="py-3 font-medium">{item.name}</td>
                        <td className="py-3">{fmt(item.quantity)}</td>
                        <td className="py-3">{fmt(item.revenue)} {t('common.sar')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <EmptyState text={t('orderAnalytics.noData')} />}
          </CardContent>
        </Card>
      </div>
    </ModuleLayout>
  );
}
